/**
 * User, Role, and Permission type definitions for RWA Terminal
 *
 * This module defines the core authentication and authorization types
 * used throughout the application for role-based access control (RBAC).
 */

/**
 * Available user roles in the system.
 * Each role has a specific set of permissions:
 * - analyst: Can create assets, upload files, edit facts, create snapshots/versions
 * - reviewer: Analyst permissions + can confirm facts, resolve conflicts, review judgements
 * - publisher: Full permissions including publishing and user management
 * - readonly: View-only access, no write operations
 */
export type Role = 'analyst' | 'reviewer' | 'publisher' | 'readonly';

/**
 * User status indicating account state
 */
export type UserStatus = 'active' | 'inactive';

/**
 * Represents a user in the RWA Terminal system
 */
export interface User {
  /** Unique user identifier */
  id: string;
  /** User's email address */
  email: string;
  /** User's display name */
  name: string;
  /** User's assigned role determining their permissions */
  role: Role;
  /** Timestamp of user's last login */
  lastLoginAt: Date;
  /** Current account status */
  status: UserStatus;
}

/**
 * Permission flags for role-based access control.
 * Each permission corresponds to a specific action in the system.
 */
export interface Permission {
  /** Can create new assets */
  canCreateAsset: boolean;
  /** Can upload files/documents to assets */
  canUploadFiles: boolean;
  /** Can directly edit facts */
  canEditFacts: boolean;
  /** Can suggest facts for review */
  canSuggestFacts: boolean;
  /** Can confirm/approve suggested facts */
  canConfirmFacts: boolean;
  /** Can resolve conflicts between facts */
  canResolveConflicts: boolean;
  /** Can create asset snapshots */
  canCreateSnapshot: boolean;
  /** Can create risk assessment versions */
  canCreateRiskVersion: boolean;
  /** Can create pricing versions */
  canCreatePricingVersion: boolean;
  /** Can create judgement drafts */
  canCreateJudgement: boolean;
  /** Can review and approve/reject judgements */
  canReviewJudgement: boolean;
  /** Can publish judgements to make them current */
  canPublishJudgement: boolean;
  /** Can rollback published judgements to previous versions */
  canRollback: boolean;
  /** Can manage users and their roles (admin) */
  canManageUsers: boolean;
}

/**
 * Role-to-permission mapping for the 4 user roles.
 * Used by the usePermissions hook to determine user capabilities.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission> = {
  analyst: {
    canCreateAsset: true,
    canUploadFiles: true,
    canEditFacts: true,
    canSuggestFacts: true,
    canConfirmFacts: false,
    canResolveConflicts: false, // Can only suggest, not confirm
    canCreateSnapshot: true,
    canCreateRiskVersion: true,
    canCreatePricingVersion: true,
    canCreateJudgement: true,
    canReviewJudgement: false,
    canPublishJudgement: false,
    canRollback: false,
    canManageUsers: false,
  },
  reviewer: {
    canCreateAsset: true,
    canUploadFiles: true,
    canEditFacts: true,
    canSuggestFacts: true,
    canConfirmFacts: true,
    canResolveConflicts: true,
    canCreateSnapshot: true,
    canCreateRiskVersion: true,
    canCreatePricingVersion: true,
    canCreateJudgement: true,
    canReviewJudgement: true,
    canPublishJudgement: false,
    canRollback: true,
    canManageUsers: false,
  },
  publisher: {
    canCreateAsset: true,
    canUploadFiles: true,
    canEditFacts: true,
    canSuggestFacts: true,
    canConfirmFacts: true,
    canResolveConflicts: true,
    canCreateSnapshot: true,
    canCreateRiskVersion: true,
    canCreatePricingVersion: true,
    canCreateJudgement: true,
    canReviewJudgement: true,
    canPublishJudgement: true,
    canRollback: true,
    canManageUsers: true,
  },
  readonly: {
    canCreateAsset: false,
    canUploadFiles: false,
    canEditFacts: false,
    canSuggestFacts: false,
    canConfirmFacts: false,
    canResolveConflicts: false,
    canCreateSnapshot: false,
    canCreateRiskVersion: false,
    canCreatePricingVersion: false,
    canCreateJudgement: false,
    canReviewJudgement: false,
    canPublishJudgement: false,
    canRollback: false,
    canManageUsers: false,
  },
};

/**
 * Default landing page for each role after login
 */
export const DEFAULT_LANDING_PAGE: Record<Role, string> = {
  analyst: '/tasks',
  reviewer: '/judgements',
  publisher: '/dashboard',
  readonly: '/assets',
};
